/**
 * Catálogo de serviços e pedidos do cliente (rescisão, férias, nota fiscal…).
 * O cliente pede pelo portal ou a equipe registra o que chegou por telefone;
 * cada pedido vira uma tarefa avulsa ou um processo de fluxo.
 */
import { z } from 'zod';
import { DataISO, Id } from './comum.js';
import { CampoFluxo, Dados } from './fluxo.js';
import { Anexo } from './detalhe.js';

export const AcaoServico = z.enum(['TAREFA', 'PROCESSO']);
export type AcaoServico = z.infer<typeof AcaoServico>;

export const ServicoBody = z.object({
  nome: z.string().trim().min(2).max(120),
  /** aparece no portal, explica o que o cliente precisa informar */
  descricao: z.string().trim().max(500).nullable().default(null),
  departamentoSigla: z.string().trim().max(8).nullable().default(null),
  acao: AcaoServico.default('TAREFA'),
  fluxoId: Id.nullable().default(null),
  /** obrigação por evento do catálogo (férias, rescisão): o vencimento sai da regra legal */
  obrigacaoId: Id.nullable().default(null),
  /** chave do campo que traz a data do evento (início do gozo, desligamento…) */
  campoDataEvento: z.string().trim().max(40).nullable().default(null),
  /** prazo em dias úteis quando não há obrigação por evento */
  prazoDiasUteis: z.number().int().min(0).max(120).default(5),
  campos: z.array(CampoFluxo).max(30).default([]),
  visivelPortal: z.boolean().default(true),
  ativo: z.boolean().default(true),
  ordem: z.number().int().min(0).max(999).default(0),
});
export type ServicoBody = z.infer<typeof ServicoBody>;
export const Servico = ServicoBody.extend({ id: Id, fluxoNome: z.string().nullable(), obrigacaoSigla: z.string().nullable(), pedidosAbertos: z.number().int(), criadoEm: z.string() });
export type Servico = z.infer<typeof Servico>;

export const StatusPedido = z.enum(['ABERTO', 'EM_ANDAMENTO', 'CONCLUIDO', 'RECUSADO', 'CANCELADO']);
export type StatusPedido = z.infer<typeof StatusPedido>;
export const OrigemPedido = z.enum(['PORTAL', 'ESCRITORIO']);
export type OrigemPedido = z.infer<typeof OrigemPedido>;
export const CanalPedido = z.enum(['TELEFONE', 'EMAIL', 'WHATSAPP', 'PRESENCIAL', 'OUTRO']);
export type CanalPedido = z.infer<typeof CanalPedido>;

/** Escritório registra um pedido que chegou por fora (telefone, e-mail…). */
export const PedidoBody = z.object({
  clienteId: Id,
  servicoId: Id,
  dados: Dados.default({}),
  observacao: z.string().trim().max(2000).nullable().default(null),
  canal: CanalPedido.default('TELEFONE'),
  /** contato do cliente que pediu (para o aviso por e-mail) */
  contatoId: Id.nullable().default(null),
  avisarCliente: z.boolean().default(true),
  /** funcionário do cliente de que o pedido trata */
  funcionarioId: Id.nullable().optional(),
});
export type PedidoBody = z.infer<typeof PedidoBody>;
/** Cliente pede pelo portal. */
export const PortalPedidoBody = z.object({ servicoId: Id, dados: Dados.default({}), observacao: z.string().trim().max(2000).nullable().default(null), /** funcionário (da lista que o portal mostra) de que o pedido trata */ funcionarioId: Id.nullable().optional() });
export type PortalPedidoBody = z.infer<typeof PortalPedidoBody>;
export const RecusarPedidoBody = z.object({ resposta: z.string().trim().min(2).max(2000) });

export const Pedido = z.object({
  id: Id, clienteId: Id, clienteNome: z.string(), servicoId: Id, servicoNome: z.string(), departamentoSigla: z.string().nullable(),
  origem: OrigemPedido, canal: CanalPedido.nullable(), contatoNome: z.string().nullable(), membroApelido: z.string().nullable(),
  status: StatusPedido, dados: Dados, campos: z.array(CampoFluxo), observacao: z.string().nullable(), dataEvento: DataISO.nullable(), vencimento: DataISO.nullable(),
  tarefaId: Id.nullable(), processoId: Id.nullable(), funcionarioId: Id.nullable().optional(), funcionarioNome: z.string().nullable().optional(), resposta: z.string().nullable(), anexos: z.array(Anexo),
  mensagens: z.array(z.object({ id: Id, de: z.enum(['CLIENTE', 'ESCRITORIO']), autorNome: z.string(), texto: z.string(), criadoEm: z.string() })),
  /** aberto e com prazo vencido */
  atrasado: z.boolean(),
  criadoEm: z.string(), concluidoEm: z.string().nullable(),
});
export const MensagemPedidoBody = z.object({ texto: z.string().trim().min(1).max(2000) });

/** Relatório de atendimento dos pedidos. */
export const PedidosRelatorioGrupo = z.object({ chave: z.string(), nome: z.string(), pedidos: z.number().int(), abertos: z.number().int(), atrasados: z.number().int(), concluidos: z.number().int(), recusados: z.number().int(), noPrazo: z.number().int(), mediaDias: z.number().nullable() });
export const PedidosRelatorioResposta = z.object({ periodo: z.object({ de: DataISO, ate: DataISO }), porServico: z.array(PedidosRelatorioGrupo), porDepartamento: z.array(PedidosRelatorioGrupo), totais: PedidosRelatorioGrupo });
export type PedidosRelatorioResposta = z.infer<typeof PedidosRelatorioResposta>;
export type Pedido = z.infer<typeof Pedido>;
export const PedidosQuery = z.object({ status: StatusPedido.optional(), clienteId: Id.optional(), abertos: z.enum(['1', '0']).optional() });
export type PedidosQuery = z.infer<typeof PedidosQuery>;

/** O que o portal mostra para o cliente escolher. */
/** O que o portal mostra de um funcionário: só o que o próprio cliente (empregador) precisa para apontar de quem fala. Nada de CPF ou salário. */
export const PortalFuncionario = z.object({ id: Id, nome: z.string(), cargo: z.string().nullable() });
export type PortalFuncionario = z.infer<typeof PortalFuncionario>;
export const PortalServico = z.object({ id: Id, nome: z.string(), descricao: z.string().nullable(), departamentoSigla: z.string().nullable(), campos: z.array(CampoFluxo) });
export type PortalServico = z.infer<typeof PortalServico>;

/** Bloco no detalhe da tarefa quando ela nasceu de um pedido. */
export const TarefaPedido = z.object({ id: Id, servicoNome: z.string(), origem: OrigemPedido, canal: CanalPedido.nullable(), contatoNome: z.string().nullable(), status: StatusPedido, criadoEm: z.string() });
export type TarefaPedido = z.infer<typeof TarefaPedido>;
